// Firebase Configuration and Basic Functions
// Replace with your Firebase project config
import { initializeApp } from 'firebase/app';
import {
    getAuth,
    onAuthStateChanged,
    signOut
} from 'firebase/auth';
import {
    getFirestore,
    doc,
    getDoc,
    getDocs,
    collection,
    query,
    where,
    setDoc,
    updateDoc,
    deleteDoc,
    writeBatch,
    increment,
    serverTimestamp
} from 'firebase/firestore';
import config from './config/env.js';
import { cache, generateCacheKey } from './utils/cache.js';

// Firebase Configuration
const firebaseConfig = config.firebase;

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Export auth and db for use in other services
export { auth, db };

// Basic auth functions only
export const firebaseAuth = {
    // Get current user
    getCurrentUser: () => auth.currentUser,

    // Listen to auth state changes
    onAuthChange: (callback) => onAuthStateChanged(auth, callback),

    // Sign out
    signOut: () => signOut(auth)
};

// Basic database functions
export const firebaseDB = {
    // Users
    getUser: async (userId) => {
        const cacheKey = generateCacheKey('getUser', { userId });
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const docRef = doc(db, 'users', userId);
        const docSnap = await getDoc(docRef);
        const result = docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
        cache.set(cacheKey, result);
        return result;
    },

    getAllStudents: async () => {
        const cacheKey = 'getAllStudents';
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const q = query(collection(db, 'users'), where('role', '==', 'student'));
        const snapshot = await getDocs(q);
        const result = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        cache.set(cacheKey, result);
        return result;
    },

    // Flashcard Sets
    createFlashcardSet: async (data) => {
        const docRef = doc(collection(db, 'flashcard_sets'));
        await setDoc(docRef, { ...data, createdAt: serverTimestamp() });
        return docRef.id;
    },

    getFlashcardSets: async () => {
        const snapshot = await getDocs(collection(db, 'flashcard_sets'));
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    getFlashcardSet: async (setId) => {
        const docRef = doc(db, 'flashcard_sets', setId);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
    },

    updateFlashcardSet: async (setId, data) => {
        const docRef = doc(db, 'flashcard_sets', setId);
        await updateDoc(docRef, data);
    },

    deleteFlashcardSet: async (setId) => {
        await deleteDoc(doc(db, 'flashcard_sets', setId));
    },

    // Flashcards
    createFlashcard: async (setId, data) => {
        const docRef = doc(collection(db, 'flashcards'));
        await setDoc(docRef, { ...data, setId, createdAt: serverTimestamp() });
        return docRef.id;
    },

    getFlashcardsBySet: async (setId) => {
        const q = query(collection(db, 'flashcards'), where('setId', '==', setId));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    updateFlashcard: async (cardId, data) => {
        const docRef = doc(db, 'flashcards', cardId);
        await updateDoc(docRef, data);
    },

    deleteFlashcard: async (cardId) => {
        await deleteDoc(doc(db, 'flashcards', cardId));
    },

    bulkCreateFlashcards: async (setId, flashcards) => {
        const batch = writeBatch(db);
        flashcards.forEach(card => {
            const docRef = doc(collection(db, 'flashcards'));
            batch.set(docRef, { ...card, setId, createdAt: serverTimestamp() });
        });
        await batch.commit();

        // Update card count
        const setRef = doc(db, 'flashcard_sets', setId);
        const currentCards = await getDocs(query(collection(db, 'flashcards'), where('setId', '==', setId)));
        await updateDoc(setRef, { cardCount: currentCards.size });
    },

    // Quiz Sets
    createQuizSet: async (data) => {
        const docRef = doc(collection(db, 'quiz_sets'));
        await setDoc(docRef, { ...data, createdAt: serverTimestamp() });
        return docRef.id;
    },

    getQuizSets: async () => {
        const snapshot = await getDocs(collection(db, 'quiz_sets'));
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    getQuizSet: async (setId) => {
        const docRef = doc(db, 'quiz_sets', setId);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
    },

    deleteQuizSet: async (setId) => {
        await deleteDoc(doc(db, 'quiz_sets', setId));
    },

    updateQuizSet: async (setId, data) => {
        const docRef = doc(db, 'quiz_sets', setId);
        await updateDoc(docRef, data);
    },

    // Quiz Questions
    createQuizQuestion: async (setId, data) => {
        const docRef = doc(collection(db, 'quiz_questions'));
        await setDoc(docRef, { ...data, setId, createdAt: serverTimestamp() });
        return docRef.id;
    },

    getQuestionsBySet: async (setId) => {
        const q = query(collection(db, 'quiz_questions'), where('setId', '==', setId));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    deleteQuizQuestion: async (questionId) => {
        await deleteDoc(doc(db, 'quiz_questions', questionId));
    },

    updateQuizQuestion: async (questionId, data) => {
        const docRef = doc(db, 'quiz_questions', questionId);
        await updateDoc(docRef, data);
    },

    bulkCreateQuizQuestions: async (setId, questions) => {
        const batch = writeBatch(db);
        questions.forEach(q => {
            const docRef = doc(collection(db, 'quiz_questions'));
            batch.set(docRef, { ...q, setId, createdAt: serverTimestamp() });
        });
        await batch.commit();

        // Update question count
        const setRef = doc(db, 'quiz_sets', setId);
        const currentQuestions = await getDocs(query(collection(db, 'quiz_questions'), where('setId', '==', setId)));
        await updateDoc(setRef, { questionCount: currentQuestions.size });
    },

    // Lessons
    createLesson: async (data) => {
        const docRef = doc(collection(db, 'lessons'));
        await setDoc(docRef, { ...data, createdAt: serverTimestamp() });
        return docRef.id;
    },

    getLessons: async () => {
        const snapshot = await getDocs(collection(db, 'lessons'));
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    getLesson: async (lessonId) => {
        const docRef = doc(db, 'lessons', lessonId);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
    },

    updateLesson: async (lessonId, data) => {
        const docRef = doc(db, 'lessons', lessonId);
        await updateDoc(docRef, data);
    },

    deleteLesson: async (lessonId) => {
        await deleteDoc(doc(db, 'lessons', lessonId));
    },

    // Flashcard Progress (Spaced Repetition)
    getFlashcardProgress: async (userId, flashcardId) => {
        const docRef = doc(db, 'flashcard_progress', `${userId}_${flashcardId}`);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? docSnap.data() : null;
    },

    updateFlashcardProgress: async (userId, flashcardId, data) => {
        const docRef = doc(db, 'flashcard_progress', `${userId}_${flashcardId}`);
        await setDoc(docRef, { ...data, userId, flashcardId }, { merge: true });
    },

    getDueFlashcards: async (userId) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const q = query(
            collection(db, 'flashcard_progress'),
            where('userId', '==', userId),
            where('nextReviewDate', '<=', today)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => doc.data());
    },

    // Quiz Progress (Spaced Repetition)
    getQuizProgress: async (userId, questionId) => {
        const docRef = doc(db, 'quiz_progress', `${userId}_${questionId}`);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? docSnap.data() : null;
    },

    updateQuizProgress: async (userId, questionId, data) => {
        const docRef = doc(db, 'quiz_progress', `${userId}_${questionId}`);
        await setDoc(docRef, { ...data, userId, questionId }, { merge: true });
    },

    // Daily Assignments
    createAssignment: async (data) => {
        const docRef = doc(collection(db, 'daily_assignments'));
        await setDoc(docRef, { ...data, createdAt: serverTimestamp() });
        return docRef.id;
    },

    getAssignmentsForDate: async (date) => {
        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);

        const q = query(
            collection(db, 'daily_assignments'),
            where('assignedDate', '>=', startOfDay),
            where('assignedDate', '<=', endOfDay)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    // Daily Activity Log
    logActivity: async (userId, activityType, data) => {
        const today = new Date();
        const dateStr = today.toISOString().split('T')[0];
        const docRef = doc(db, 'daily_activity', `${userId}_${dateStr}`);

        await setDoc(docRef, {
            userId,
            date: dateStr,
            [activityType]: data,
            updatedAt: serverTimestamp()
        }, { merge: true });
    },

    getActivityForDate: async (userId, date) => {
        const dateStr = date.toISOString().split('T')[0];
        const docRef = doc(db, 'daily_activity', `${userId}_${dateStr}`);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? docSnap.data() : null;
    },

    // Badges
    awardBadge: async (userId, badgeType) => {
        const docRef = doc(db, 'badges', `${userId}_${badgeType}`);
        await setDoc(docRef, {
            userId,
            badgeType,
            earnedAt: serverTimestamp()
        });
    },

    getUserBadges: async (userId) => {
        const q = query(collection(db, 'badges'), where('userId', '==', userId));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => doc.data());
    },

    // XP and Level
    addXP: async (userId, amount) => {
        // Import XP_PER_LEVEL dynamically to avoid circular dependency
        const { XP_PER_LEVEL } = await import('./utils/gamification.js');

        const docRef = doc(db, 'users', userId);
        await updateDoc(docRef, {
            xp: increment(amount)
        });

        // Check for level up
        const user = await getDoc(docRef);
        const userData = user.data();
        const newLevel = Math.floor(userData.xp / XP_PER_LEVEL) + 1;

        if (newLevel > userData.level) {
            await updateDoc(docRef, { level: newLevel });
            return { leveledUp: true, newLevel };
        }

        return { leveledUp: false };
    },

    // Streak
    updateStreak: async (userId) => {
        const docRef = doc(db, 'users', userId);
        const userSnap = await getDoc(docRef);
        const user = userSnap.data();

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const lastActive = user.lastActiveDate ? user.lastActiveDate.toDate() : null;

        let newStreak = user.streak || 0;

        if (lastActive) {
            const lastActiveDay = new Date(lastActive);
            lastActiveDay.setHours(0, 0, 0, 0);

            const diffDays = Math.floor((today - lastActiveDay) / (1000 * 60 * 60 * 24));

            if (diffDays === 1) {
                // Consecutive day
                newStreak += 1;
            } else if (diffDays > 1) {
                // Streak broken
                newStreak = 1;
            }
            // diffDays === 0 means same day, keep streak
        } else {
            newStreak = 1;
        }

        await updateDoc(docRef, {
            streak: newStreak,
            lastActiveDate: serverTimestamp()
        });

        return newStreak;
    }
};